// src/context/AuthContext.js
// Updated: 02/13/2026 - Optimized iOS Cold Starts with pageshow and linting fixes

import React, { useContext, useState, useEffect, useCallback } from 'react';
import { onIdTokenChanged } from 'firebase/auth';
import { auth, db } from '../services/firebase';
import { doc, onSnapshot } from 'firebase/firestore'; 
import { CircularProgress, Box } from '@mui/material';
import { initializeFCM } from '../services/pushNotificationService';

const AuthContext = React.createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [churchProfile, setChurchProfile] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);

  // Helper to safely trigger FCM logic
  const refreshPushToken = useCallback((user, profile, church, force = false) => {
    // REMOVED visibilityState requirement to fix PWA Cold Start regeneration
    if (user && profile) {
      initializeFCM(user, profile, church, force)
        .then(() => {
          if (force) console.log("FCM Sync process completed.");
        })
        .catch(err => {
          console.error("Auto-refresh of FCM token failed:", err);
        });
    }
  }, []);

  // 1. Listen for Auth Changes
  useEffect(() => {
    let unsubscribeUser = () => {};

    const unsubscribeAuth = onIdTokenChanged(auth, async (user) => {
      if (user) {
        setCurrentUser(user);
        await user.getIdTokenResult();

        const userDocRef = doc(db, 'users', user.uid);
        
        unsubscribeUser = onSnapshot(userDocRef, (docSnap) => {
          if (docSnap.exists()) {
            setUserProfile(docSnap.data());
          } else {
            setUserProfile(null);
          }
          setIsInitialized(true);
        });
      } else {
        setCurrentUser(null);
        setUserProfile(null);
        setChurchProfile(null);
        setIsInitialized(true);
        unsubscribeUser();
      }
    });

    return () => {
      unsubscribeAuth();
      unsubscribeUser();
    };
  }, []);

  // 1.5 Rapid Sync Check (Fires immediately upon login before church data)
  useEffect(() => {
    if (currentUser && userProfile) {
      refreshPushToken(currentUser, userProfile, churchProfile, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, userProfile?.uid]); // Watch UID specifically for rapid iOS thaw

  // 2. Listen for Real-time Church Data Updates
  useEffect(() => {
    // Ensure we have both a user and a profile before attempting church lookup
    if (currentUser && userProfile?.churchId) {
      const churchDocRef = doc(db, 'churches', userProfile.churchId);

      const unsubscribeChurch = onSnapshot(churchDocRef, (docSnap) => {
        if (docSnap.exists()) {
          const churchData = docSnap.data();
          setChurchProfile(churchData);
          // Trigger regeneration check on load
          refreshPushToken(currentUser, userProfile, churchData, true);
        } else {
          setChurchProfile(null);
          refreshPushToken(currentUser, userProfile, null, true);
        }
      });

      return () => unsubscribeChurch();
    } else if (currentUser && userProfile) {
      // Super Admin fallback
      setChurchProfile(null);
      refreshPushToken(currentUser, userProfile, null, true);
    }
  }, [userProfile?.churchId, userProfile, currentUser, refreshPushToken]);

  // 3. AUTO-REFRESH: Trigger on Visibility Change, Focus, AND iOS PageShow
  useEffect(() => {
    const handleActivity = () => {
      // Only check on activity if visibility is actually restored
      if (document.visibilityState === 'visible') {
        refreshPushToken(currentUser, userProfile, churchProfile, false);
      }
    };

    document.addEventListener('visibilitychange', handleActivity);
    window.addEventListener('focus', handleActivity);
    window.addEventListener('pageshow', handleActivity); // iOS Specific Trigger

    return () => {
      document.removeEventListener('visibilitychange', handleActivity);
      window.removeEventListener('focus', handleActivity);
      window.removeEventListener('pageshow', handleActivity);
    };
  }, [currentUser, userProfile, churchProfile, refreshPushToken]);

  // 4. REMOTE SYNC LISTENER (Silent)
  useEffect(() => {
    if (userProfile?.forceTokenSyncAt) {
      console.log("Remote sync signal received!");
      refreshPushToken(currentUser, userProfile, churchProfile, true);
    }
  }, [userProfile?.forceTokenSyncAt, currentUser, churchProfile, refreshPushToken]);

  // 5. PERIODIC REFRESH
  useEffect(() => {
    if (!currentUser || !userProfile || !churchProfile) return;

    const interval = setInterval(() => {
      refreshPushToken(currentUser, userProfile, churchProfile, false);
    }, 6 * 60 * 60 * 1000);

    return () => clearInterval(interval);
  }, [currentUser, userProfile, churchProfile, refreshPushToken]); // Added missing userProfile dependency

  const value = { currentUser, userProfile, churchProfile, isInitialized };

  if (!isInitialized) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}